import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_NAME = "EkkBaz AI";
const DEFAULT_IMAGE = "https://ekkbaz.com/assets/images/LogoAI.png";
const BASE_URL = "https://ekkbaz.com";

export default function BazAIHelmet({
  title,
  description,
  image,
  url,
  keywords,
  schemaType = "WebPage",
  faqItems,
  productName,
  industryName,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const canonicalUrl = url || BASE_URL;
  const ogImage = image || DEFAULT_IMAGE;

  const baseSchema = {
    "@context": "https://schema.org",
    "@type": schemaType,
    name: fullTitle,
    description: description,
    url: canonicalUrl,
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: BASE_URL,
      logo: {
        "@type": "ImageObject",
        url: DEFAULT_IMAGE,
      },
    },
  };

  const softwareSchema =
    productName
      ? {
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: productName,
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web, Android, iOS",
          description: description,
          url: canonicalUrl,
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
          },
          provider: {
            "@type": "Organization",
            name: SITE_NAME,
            url: BASE_URL,
          },
        }
      : null;

  const faqSchema =
    faqItems && faqItems.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqItems.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.answer,
            },
          })),
        }
      : null;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: BASE_URL },
      ...(industryName
        ? [
            {
              "@type": "ListItem",
              position: 2,
              name: "Industries",
              item: `${BASE_URL}/industry`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: industryName,
              item: canonicalUrl,
            },
          ]
        : productName
        ? [
            {
              "@type": "ListItem",
              position: 2,
              name: "Products",
              item: `${BASE_URL}/product`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: productName,
              item: canonicalUrl,
            },
          ]
        : []),
    ],
  };

  return (
    <Helmet>
      {/* Core */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@EkkBaz" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Robots & Indexing */}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />

      {/* Structured Data */}
      <script type="application/ld+json">{JSON.stringify(baseSchema)}</script>
      {softwareSchema && (
        <script type="application/ld+json">
          {JSON.stringify(softwareSchema)}
        </script>
      )}
      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>
  );
}
